import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { licensesApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Key, Copy, CheckCircle, XCircle, Users, Store, Clock, 
  AlertTriangle, Loader2, Building2, RefreshCw, Send, Calendar
} from 'lucide-react';

const LICENSE_PLANS = {
  mensile: { name: 'Mensile', duration: 30, price: 49, maxAziende: 10, maxPdv: 50, maxUsers: 10 },
  annuale: { name: 'Annuale', duration: 365, price: 490, maxAziende: 10, maxPdv: 50, maxUsers: 10 },
};

export default function LicensesPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin';
  
  const [myLicense, setMyLicense] = useState(null);
  const [allLicenses, setAllLicenses] = useState([]);
  const [renewalRequests, setRenewalRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('mensile');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load my license for admins
      if (isAdmin) {
        const myLicenseRes = await licensesApi.getMyLicense();
        setMyLicense(myLicenseRes.data);
      }
      
      // Load all licenses and renewal requests for super admins
      if (isSuperAdmin) {
        const [licensesRes, renewalsRes] = await Promise.all([
          licensesApi.getAll(),
          licensesApi.getRenewalRequests()
        ]);
        setAllLicenses(licensesRes.data || []);
        setRenewalRequests(renewalsRes.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLicense = async () => {
    setGenerating(true);
    try {
      const response = await licensesApi.generate(selectedPlan, selectedUserId || null);
      setGeneratedKey(response.data);
      toast.success('Chiave di licenza generata con successo!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Chiave copiata negli appunti!');
  };

  const handleRequestRenewal = async () => {
    try {
      const response = await licensesApi.requestRenewal();
      toast.success('Richiesta di rinnovo inviata!');
      setMyLicense(prev => ({ ...prev, renewalRequested: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della richiesta');
    }
  };

  const handleCompleteRenewal = async (requestId) => {
    try {
      await licensesApi.completeRenewal(requestId);
      toast.success('Richiesta completata');
      loadData();
    } catch (error) {
      toast.error('Errore nel completamento');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Admin view - show their license
  if (isAdmin) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">La Mia Licenza</h1>
          <p className="text-muted-foreground">Gestisci la tua licenza e il piano attivo</p>
        </div>

        {myLicense?.has_license ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className={myLicense.is_expired ? 'border-red-300 bg-red-50' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Key className="w-5 h-5" />
                      Piano {myLicense.plan_name}
                    </CardTitle>
                    <CardDescription>
                      {myLicense.is_expired ? (
                        <span className="text-red-600 font-semibold">Licenza Scaduta</span>
                      ) : (
                        `Scade il ${formatDate(myLicense.expires_at)}`
                      )}
                    </CardDescription>
                  </div>
                  <Badge variant={myLicense.is_expired ? 'destructive' : 'default'}>
                    {myLicense.is_expired ? 'Scaduta' : 'Attiva'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* License Key */}
                <div className="bg-muted p-4 rounded-lg">
                  <Label className="text-xs text-muted-foreground">Chiave di Licenza</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="font-mono text-lg">{myLicense.license_key}</code>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyToClipboard(myLicense.license_key)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <Calendar className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                    <div className="text-2xl font-bold text-blue-600">{myLicense.days_remaining}</div>
                    <div className="text-xs text-muted-foreground">Giorni Rimanenti</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <Building2 className="w-5 h-5 mx-auto text-green-600 mb-1" />
                    <div className="text-2xl font-bold text-green-600">{myLicense.max_aziende}</div>
                    <div className="text-xs text-muted-foreground">Max Aziende</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <Store className="w-5 h-5 mx-auto text-purple-600 mb-1" />
                    <div className="text-2xl font-bold text-purple-600">{myLicense.max_pdv}</div>
                    <div className="text-xs text-muted-foreground">Max UL</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <Users className="w-5 h-5 mx-auto text-orange-600 mb-1" />
                    <div className="text-2xl font-bold text-orange-600">{myLicense.max_users}</div>
                    <div className="text-xs text-muted-foreground">Max Utenti</div>
                  </div>
                </div>

                {/* Renewal Button */}
                {myLicense.is_expired || myLicense.days_remaining < 30 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">
                        {myLicense.is_expired ? 'La tua licenza è scaduta' : 'La licenza scadrà presto'}
                      </span>
                    </div>
                    <Button onClick={handleRequestRenewal} className="w-full">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Richiedi Rinnovo Licenza
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Il Super Admin riceverà una notifica e ti invierà una nuova chiave
                    </p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center">
              <Key className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nessuna licenza attiva</h3>
              <p className="text-muted-foreground">
                Contatta il Super Admin per ottenere una licenza
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Super Admin view
  if (isSuperAdmin) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Gestione Licenze</h1>
            <p className="text-muted-foreground">Genera e gestisci le licenze degli utenti</p>
          </div>
          <Button onClick={() => { setGeneratedKey(null); setGenerateDialogOpen(true); }}>
            <Key className="w-4 h-4 mr-2" />
            Genera Nuova Licenza
          </Button>
        </div>

        <Tabs defaultValue="requests" className="space-y-4">
          <TabsList>
            <TabsTrigger value="requests">
              Richieste Rinnovo
              {renewalRequests.length > 0 && (
                <Badge variant="destructive" className="ml-2">{renewalRequests.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all">Tutte le Licenze ({allLicenses.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="requests">
            {renewalRequests.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-semibold">Nessuna richiesta in attesa</h3>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {renewalRequests.map((request) => (
                  <Card key={request.id} className="border-amber-200 bg-amber-50">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{request.user_name}</h4>
                          <p className="text-sm text-muted-foreground">{request.user_email}</p>
                          <p className="text-sm mt-1">
                            Codice: <code className="bg-white px-2 py-1 rounded">{request.renewal_code}</code>
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Richiesto il {formatDate(request.created_at)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline"
                            onClick={() => {
                              setSelectedUserId(request.user_id);
                              setGeneratedKey(null);
                              setGenerateDialogOpen(true);
                            }}
                          >
                            <Key className="w-4 h-4 mr-2" />
                            Genera Chiave
                          </Button>
                          <Button 
                            variant="ghost"
                            onClick={() => handleCompleteRenewal(request.id)}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all">
            {allLicenses.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Key className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Nessuna licenza</h3>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {allLicenses.map((license) => {
                  const isExpired = new Date(license.expires_at) < new Date();
                  return (
                    <Card key={license.id} className={isExpired ? 'opacity-60' : ''}>
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <code className="font-mono bg-muted px-2 py-1 rounded">{license.license_key}</code>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => copyToClipboard(license.license_key)}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {license.customer_email || 'Non assegnata'} | {license.plan_name}
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge variant={isExpired ? 'destructive' : 'default'}>
                              {isExpired ? 'Scaduta' : 'Attiva'}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-1">
                              Scade: {formatDate(license.expires_at)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Generate License Dialog */}
        <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Genera Nuova Licenza</DialogTitle>
            </DialogHeader>
            
            {generatedKey ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-3" />
                  <h4 className="font-semibold text-green-800 mb-2">Licenza Generata!</h4>
                  <div className="bg-white border rounded p-3 mb-3">
                    <code className="text-lg font-mono">{generatedKey.license_key}</code>
                  </div>
                  <Button onClick={() => copyToClipboard(generatedKey.license_key)} className="w-full">
                    <Copy className="w-4 h-4 mr-2" />
                    Copia Chiave
                  </Button>
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p><strong>Piano:</strong> {generatedKey.plan_name}</p>
                  <p><strong>Durata:</strong> {generatedKey.duration_days} giorni</p>
                  <p><strong>Scadenza:</strong> {formatDate(generatedKey.expires_at)}</p>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  Invia questa chiave all'utente. Potrà inserirla nella pagina di login.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Piano Licenza</Label>
                  <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensile">
                        <div className="flex justify-between items-center w-full">
                          <span>Mensile</span>
                          <span className="text-muted-foreground ml-4">30 giorni - €49</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="annuale">
                        <div className="flex justify-between items-center w-full">
                          <span>Annuale</span>
                          <span className="text-muted-foreground ml-4">365 giorni - €490</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-muted p-3 rounded-lg text-sm space-y-1">
                  <p><strong>Limiti del piano:</strong></p>
                  <p>• Max 10 Aziende</p>
                  <p>• Max 50 Unità Locali</p>
                  <p>• Max 10 Utenti</p>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button onClick={handleGenerateLicense} disabled={generating}>
                    {generating ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Key className="w-4 h-4 mr-2" />
                    )}
                    Genera Chiave
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Other roles - no access
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Card>
        <CardContent className="py-8 text-center">
          <XCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-semibold">Accesso non consentito</h3>
          <p className="text-muted-foreground">Non hai i permessi per visualizzare questa pagina</p>
        </CardContent>
      </Card>
    </div>
  );
}
